import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { useDebouncedValue } from "./useDebouncedValue";

/** Mirror delay. Long enough that a burst of typing settles into ONE history write. */
export const URL_PARAM_MIRROR_DELAY_MS = 400;

/**
 * What the mirror should do about one param: `null` when the URL already says it, otherwise the value to
 * write (`""` meaning "remove the param").
 *
 * Pure on purpose — the whole decision of the hook lives here, so it is covered by the `unit` project without
 * a DOM. The equality guard is the part that matters: a value already in the URL must produce no write at
 * all, which is what keeps the hook safe to call on every render.
 */
export function resolveMirrorWrite(
  current: string | null,
  value: string | null | undefined,
): { value: string } | null {
  const next = value?.trim() ?? "";
  return (current ?? "") === next ? null : { value: next };
}

/**
 * Mirror a live piece of component state into ONE search param, with `replace`.
 *
 * The URL is a shareable snapshot of a view, not a keystroke log. Writing it straight from an input's
 * `onChange` — which is what the cards and holders lists used to do — spends one `history.replaceState` per
 * character typed, and WebKit throws `SecurityError: Attempt to use history.replaceState() more than 100
 * times per 10 seconds` (COLOCA-DASHBOARD-1J). Holding backspace over a long search term reaches that on its
 * own.
 *
 * That error is not cosmetic, which is why this hook exists instead of a bigger debounce at each call site.
 * react-router wraps `pushState` in a try/catch and falls back to a full page load, but `replaceState` has no
 * catch, and the write happens inside `completeNavigation` BEFORE `updateState` — so the throw leaves the
 * router mid-navigation: `navigation` never returns to idle, the location stays stale, and every later
 * `navigate()` is dead. The user's next move is to click a button that no longer responds, which is the rage
 * click Sentry logged three minutes after the error.
 *
 * Debouncing bounds the write rate to roughly one per pause in typing; the equality guard means a settled
 * value writes nothing. Both together make it impossible for a search field to reach the limit.
 *
 * Seeding stays the caller's job and stays read-once: this hook only ever writes state INTO the URL, so an
 * externally changed param is re-asserted from state rather than adopted — the contract the cards and holders
 * lists already documented.
 */
export function useUrlParamMirror(
  key: string,
  value: string | null | undefined,
  delayMs: number = URL_PARAM_MIRROR_DELAY_MS,
): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const debounced = useDebouncedValue(value?.trim() ?? "", delayMs);
  const current = searchParams.get(key);

  useEffect(() => {
    const write = resolveMirrorWrite(current, debounced);
    if (!write) return;

    setSearchParams(
      (prev) => {
        if (write.value) {
          prev.set(key, write.value);
        } else {
          prev.delete(key);
        }
        return prev;
      },
      { replace: true },
    );
  }, [current, debounced, key, setSearchParams]);
}
